import {
    useCallback,
    useState
} from "react";
import {
    TouchableOpacity,
    View
} from "react-native";
import type IFetchInstagramModalProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    TextInput,
    Button,
    Modal,
    Text
} from "ncore-ui-kit";
import type {
    PostPhotoItem
} from "../postPhotosManager/type";
import {
    XIcon
} from "lucide-react-native";

type InstagramPostDetails = {
    imageUrl: string | null;
    imageUrls?: string[];
    caption: string;
};

const extractInstagramShortcode = (url: string): string | null => {
    if(!url.includes("instagram.com")) {
        return null;
    }
    const match = url.match(/(?:p|reel|tv)\/([A-Za-z0-9_-]+)/i);
    return match && match[1] ? match[1] : null;
};

const cleanInstagramCaption = (rawDescription: string): string => {
    let text = rawDescription.trim();
    text = text.replace(/<a class="CaptionUsername"[^>]*>[\s\S]*?<\/a>/gi, "");
    text = text.replace(/<br\s*\/?>/gi, "\n");
    text = text.replace(/<[^>]*>/g, "");
    text = text.replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, "\"").replace(/&#39;/g, "'");

    const quotedMatch = text.match(/:\s*[“"]([\s\S]+)[”"]\.?$/);
    if(quotedMatch && quotedMatch[1]) {
        text = quotedMatch[1];
    }

    return text.trim();
};

const isExcludedImageUrl = (targetUrl: string): boolean => {
    const lower = targetUrl.toLowerCase();
    return (
        lower.includes("static.cdninstagram.com") ||
        lower.includes("profile_pic") ||
        lower.includes("t51.2885-19") ||
        lower.includes("/s100x100_") ||
        lower.includes("/s150x150_") ||
        lower.includes("/s320x320_") ||
        lower.includes("/150x150/") ||
        lower.includes("/320x320/") ||
        lower.includes("rsrc.php") ||
        lower.includes("favicon") ||
        lower.includes("assets") ||
        lower.includes("logo")
    );
};

const parseInstagramHtml = (
    targetShortcode: string,
    html: string
): InstagramPostDetails | null => {
    const scriptRegex = /<script\b[^>]*>([\s\S]*?)<\/script>/gi;
    let match: RegExpExecArray | null = null;
    const imageUrls: string[] = [];
    let caption = "";

    while((match = scriptRegex.exec(html)) !== null) {
        const content = match[1];
        if(!content) {
            continue;
        }

        if(content.includes("carousel_media") || content.includes("xdt_api__v1__media__shortcode__web_info") || content.includes("edge_sidecar_to_children") || content.includes(targetShortcode)) {
            try {
                const data = JSON.parse(content);

                const findData = (obj: unknown) => {
                    if(!obj || typeof obj !== "object") {
                        return;
                    }

                    const record = obj as Record<string, unknown>;

                    if(record.code === targetShortcode || (Array.isArray(record.carousel_media) && imageUrls.length === 0)) {
                        if(Array.isArray(record.carousel_media)) {
                            for(const m of record.carousel_media) {
                                if(m && typeof m === "object") {
                                    const item = m as Record<string, unknown>;
                                    const imageVersions = item.image_versions2 as { candidates?: { url?: string }[] } | undefined;
                                    const bestCandidate = imageVersions?.candidates?.[0]?.url;
                                    const targetUrl = bestCandidate || (item.display_uri as string) || (item.display_url as string);
                                    if(targetUrl && !isExcludedImageUrl(targetUrl) && !imageUrls.includes(targetUrl)) {
                                        imageUrls.push(targetUrl);
                                    }
                                }
                            }
                        }

                        const sidecar = record.edge_sidecar_to_children as { edges?: { node?: Record<string, unknown> }[] } | undefined;
                        if(sidecar && Array.isArray(sidecar.edges)) {
                            for(const edge of sidecar.edges) {
                                const node = edge?.node;
                                if(node) {
                                    const imageVersions = node.image_versions2 as { candidates?: { url?: string }[] } | undefined;
                                    const bestCandidate = imageVersions?.candidates?.[0]?.url;
                                    const targetUrl = bestCandidate || (node.display_url as string) || (node.display_uri as string);
                                    if(targetUrl && !isExcludedImageUrl(targetUrl) && !imageUrls.includes(targetUrl)) {
                                        imageUrls.push(targetUrl);
                                    }
                                }
                            }
                        }

                        if(imageUrls.length === 0) {
                            const singleVersions = record.image_versions2 as { candidates?: { url?: string }[] } | undefined;
                            const bestSingle = singleVersions?.candidates?.[0]?.url || (record.display_uri as string) || (record.display_url as string);
                            if(bestSingle && !isExcludedImageUrl(bestSingle) && !imageUrls.includes(bestSingle)) {
                                imageUrls.push(bestSingle);
                            }
                        }

                        if(record.caption && typeof record.caption === "object") {
                            const capObj = record.caption as { text?: string };
                            if(typeof capObj.text === "string" && !caption) {
                                caption = capObj.text;
                            }
                        }

                        const captionEdge = record.edge_media_to_caption as { edges?: { node?: { text?: string } }[] } | undefined;
                        if(captionEdge?.edges?.[0]?.node?.text && !caption) {
                            caption = captionEdge.edges[0].node.text;
                        }
                    }

                    for(const key of Object.keys(record)) {
                        findData(record[key]);
                    }
                };

                findData(data);
            } catch {
                continue;
            }
        }
    }

    if(imageUrls.length === 0) {
        const embeddedMediaMatch = html.match(/<img[^>]+class=["'][^"']*EmbeddedMediaImage[^"']*["'][^>]+src=["']([^"']+)["']/i)
            || html.match(/<img[^>]+src=["']([^"']+)["'][^>]+class=["'][^"']*EmbeddedMediaImage[^"']*["']/i);

        if(embeddedMediaMatch && embeddedMediaMatch[1]) {
            const cleanEmbeddedUrl = embeddedMediaMatch[1].replace(/&amp;/g, "&");
            if(!isExcludedImageUrl(cleanEmbeddedUrl)) {
                imageUrls.push(cleanEmbeddedUrl);
            }
        }
    }

    if(imageUrls.length === 0) {
        const ogImageMatch = html.match(/<meta\s+property="og:image"\s+content="([^"]+)"/i) || html.match(/<meta\s+content="([^"]+)"\s+property="og:image"/i);
        if(ogImageMatch && ogImageMatch[1]) {
            const rawOgUrl = ogImageMatch[1].replace(/&amp;/g, "&");
            if(!isExcludedImageUrl(rawOgUrl)) {
                imageUrls.push(rawOgUrl);
            }
        }
    }

    if(!caption) {
        const ogDescMatch = html.match(/<meta\s+property="og:description"\s+content="([^"]+)"/i) || html.match(/<meta\s+content="([^"]+)"\s+property="og:description"/i);
        if(ogDescMatch && ogDescMatch[1]) {
            caption = ogDescMatch[1];
        }
    }

    if(imageUrls.length > 0 || caption) {
        return {
            caption: cleanInstagramCaption(caption),
            imageUrl: imageUrls[0] || null,
            imageUrls
        };
    }

    return null;
};

const parseJinaMarkdown = (text: string): { caption: string; imageUrls: string[]; } => {
    let textForImages = text;
    const cutOffPatterns = [
        /view more on instagram/i,
        /instagram'da daha fazla/i,
        /daha fazla g[oö]nderi/i,
        /di[gğ]er g[oö]nderiler/i,
        /\[add a comment/i,
        /more posts from/i,
        /more from/i
    ];

    for(const pattern of cutOffPatterns) {
        const patternMatch = pattern.exec(textForImages);
        if(patternMatch && patternMatch.index !== -1) {
            textForImages = textForImages.substring(0, patternMatch.index);
        }
    }

    const imageUrls: string[] = [];
    const carouselRegex = /\*\s+!\[(?:[^\]]*)\]\((https?:\/\/[^\s)]+)\)/g;
    let match: RegExpExecArray | null = null;

    while((match = carouselRegex.exec(textForImages)) !== null) {
        const targetUrl = match[1];
        if(targetUrl && !isExcludedImageUrl(targetUrl) && !imageUrls.includes(targetUrl)) {
            imageUrls.push(targetUrl);
        }
    }

    if(imageUrls.length === 0) {
        const singleRegex = /!\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)/g;
        while((match = singleRegex.exec(textForImages)) !== null) {
            const altText = (match[1] || "").toLowerCase();
            const targetUrl = match[2];
            if(
                altText.includes("profile") ||
                altText.includes("profil") ||
                altText.includes("avatar") ||
                altText.includes("logo")
            ) {
                continue;
            }
            if(targetUrl && !isExcludedImageUrl(targetUrl)) {
                imageUrls.push(targetUrl);
                break;
            }
        }
    }

    const commentIdx = text.indexOf("[Add a comment...");
    const postSec = commentIdx !== -1 ? text.substring(0, commentIdx) : text;
    const viewMoreIdx = postSec.lastIndexOf("[View more on Instagram]");
    let targetSection = postSec;
    if(viewMoreIdx !== -1) {
        const afterViewMore = postSec.substring(viewMoreIdx);
        const urlCloseIdx = afterViewMore.indexOf(")");
        if(urlCloseIdx !== -1) {
            targetSection = afterViewMore.substring(urlCloseIdx + 1);
        } else {
            targetSection = afterViewMore.substring("[View more on Instagram]".length);
        }
    }

    let cleaned = targetSection.replace(/\[([^\]]+)\]\([^)]+\)/g, " $1 ");
    cleaned = cleaned.replace(/\(https?:\/\/[^)]+\)/g, " ");
    const rawLines = cleaned.split("\n").map((line) => line.trim()).filter(Boolean);

    const captionLines: string[] = [];
    let passedMeta = false;

    for(const line of rawLines) {
        if(!passedMeta) {
            if(
                line.startsWith("(") ||
                line.startsWith("http") ||
                line.includes("likes") ||
                line.includes("followers") ||
                line.includes("View profile") ||
                line.includes("Like") ||
                line.includes("Comment") ||
                line.includes("Share") ||
                line.includes("Save")
            ) {
                continue;
            }
            if(!line.includes(" ") && line.length < 30) {
                passedMeta = true;
                continue;
            }
            passedMeta = true;
        }
        captionLines.push(line);
    }

    return {
        caption: cleanInstagramCaption(captionLines.join("\n\n")),
        imageUrls
    };
};

const fetchInstagramPostData = async (shortcode: string): Promise<InstagramPostDetails | null> => {
    const embedUrl = `https://www.instagram.com/p/${shortcode}/embed/captioned/`;
    const postUrl = `https://www.instagram.com/p/${shortcode}/`;

    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 9000);
        const response = await fetch(`https://r.jina.ai/${postUrl}`, {
            headers: {
                "X-Return-Format": "html"
            },
            signal: controller.signal
        });
        clearTimeout(timeoutId);
        if(response.ok) {
            const html = await response.text();
            if(html) {
                const parsed = parseInstagramHtml(shortcode, html);
                if(parsed && (parsed.imageUrls?.length || 0) > 0) {
                    return parsed;
                }
            }
        }
    } catch {
        void 0;
    }

    try {
        const jinaController = new AbortController();
        const jinaTimeoutId = setTimeout(() => jinaController.abort(), 7000);
        const jinaResponse = await fetch(`https://r.jina.ai/${embedUrl}`, {
            signal: jinaController.signal
        });
        clearTimeout(jinaTimeoutId);
        if(jinaResponse.ok) {
            const text = await jinaResponse.text();
            if(text) {
                const parsed = parseJinaMarkdown(text);
                if(parsed.imageUrls.length > 0 || parsed.caption) {
                    return {
                        imageUrl: parsed.imageUrls[0] || null,
                        imageUrls: parsed.imageUrls,
                        caption: parsed.caption
                    };
                }
            }
        }
    } catch {
        void 0;
    }

    try {
        const embedQuery = `https://api.microlink.io/?url=${encodeURIComponent(embedUrl)}&data.uncroppedImage.selector=img.EmbeddedMediaImage&data.uncroppedImage.attr=src&data.caption.selector=.Caption&data.caption.type=text`;
        const embedResponse = await fetch(embedQuery);
        if(embedResponse.ok) {
            const json = await embedResponse.json();
            if(json && json.status === "success" && json.data) {
                const uncroppedUrl = json.data.uncroppedImage ? String(json.data.uncroppedImage).replace(/&amp;/g, "&") : null;
                const captionText = json.data.caption || json.data.description || "";
                if(uncroppedUrl) {
                    return {
                        caption: cleanInstagramCaption(captionText),
                        imageUrls: [uncroppedUrl],
                        imageUrl: uncroppedUrl
                    };
                }
            }
        }
    } catch {
        void 0;
    }

    try {
        const microlinkUrl = `https://api.microlink.io/?url=${encodeURIComponent(postUrl)}`;
        const microResponse = await fetch(microlinkUrl);
        if(microResponse.ok) {
            const json = await microResponse.json();
            if(json && json.status === "success" && json.data) {
                const rawDesc = json.data.description || json.data.title || "";
                const imageUrl = json.data.image?.url || null;
                return {
                    caption: cleanInstagramCaption(rawDesc),
                    imageUrls: imageUrl ? [imageUrl] : [],
                    imageUrl
                };
            }
        }
    } catch {
        void 0;
    }

    return null;
};

const downloadImageBlob = async (imageUrl: string): Promise<Blob | null> => {
    const cleanUrl = imageUrl.replace(/&amp;/g, "&").trim();
    const downloadUrls = [
        `https://images.weserv.nl/?url=${encodeURIComponent(cleanUrl)}`,
        `https://wsrv.nl/?url=${encodeURIComponent(cleanUrl)}`,
        cleanUrl
    ];

    for(const targetUrl of downloadUrls) {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 6000);
            const response = await fetch(targetUrl, {
                signal: controller.signal
            });
            clearTimeout(timeoutId);
            const rawContentType = response.headers.get("content-type") || "";
            const mimeType = (rawContentType.split(";")[0] ?? "").trim().toLowerCase();
            if(response.ok && mimeType.startsWith("image/")) {
                const blob = await response.blob();
                if(blob && blob.size > 0) {
                    const finalType = blob.type && blob.type.startsWith("image/") ? blob.type : mimeType;
                    return new Blob([blob], {
                        type: finalType
                    });
                }
            }
        } catch {
            continue;
        }
    }

    return null;
};

const FetchInstagramModal = ({
    isVisible,
    onSuccess,
    onClose
}: IFetchInstagramModalProps) => {
    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [
        url,
        setUrl
    ] = useState<string>("");

    const [
        isLoading,
        setIsLoading
    ] = useState<boolean>(false);

    const handleReset = useCallback(() => {
        setIsLoading(false);
        setUrl("");
        onClose();
    }, [
        onClose
    ]);

    const handleFetch = async () => {
        const trimmedUrl = url.trim();
        if(!trimmedUrl) {
            NCoreUIKitSnackBar.open({
                title: localize("instagram-fetch-empty-url")
            });
            return;
        }

        const shortcode = extractInstagramShortcode(trimmedUrl);
        if(!shortcode) {
            NCoreUIKitSnackBar.open({
                title: localize("instagram-fetch-invalid-url")
            });
            return;
        }

        try {
            setIsLoading(true);

            const postData = await fetchInstagramPostData(shortcode);
            if(!postData) {
                NCoreUIKitSnackBar.open({
                    title: localize("instagram-fetch-error")
                });
                return;
            }

            const {
                imageUrls,
                imageUrl,
                caption
            } = postData;

            const allImageUrls = imageUrls && imageUrls.length > 0 ? imageUrls : (imageUrl ? [imageUrl] : []);
            const cleanImageUrls = allImageUrls.map((u) => u.replace(/&amp;/g, "&").trim()).filter(Boolean);
            const targetFirstUrl = cleanImageUrls[0] || (imageUrl ? imageUrl.replace(/&amp;/g, "&").trim() : "");

            let coverFile: File | undefined = undefined;
            let coverPreview: string = "";

            if(targetFirstUrl) {
                try {
                    const blob = await downloadImageBlob(targetFirstUrl);
                    if(blob) {
                        const extension = blob.type.includes("png") ? "png" : "jpg";
                        const fileName = `instagram_${shortcode}_0_${Date.now()}.${extension}`;
                        coverFile = new File([blob], fileName, {
                            type: blob.type || "image/jpeg"
                        });
                        coverPreview = URL.createObjectURL(blob);
                    }
                } catch (imgError) {
                    console.error(imgError);
                }

                if(!coverPreview) {
                    coverPreview = `https://wsrv.nl/?url=${encodeURIComponent(targetFirstUrl)}`;
                }
            }

            const cleanLines = caption.split("\n").map((line) => line.trim()).filter(Boolean);
            let suggestedTitle = cleanLines[0] || "";
            if(suggestedTitle.length < 25 && cleanLines.length > 1) {
                suggestedTitle = `${suggestedTitle} - ${cleanLines[1]}`;
            }
            if(suggestedTitle.length > 100) {
                suggestedTitle = `${suggestedTitle.substring(0, 97)}...`;
            }

            const shortContent = caption.length > 200 ? `${caption.substring(0, 197)}...` : caption;

            const postPhotos: PostPhotoItem[] = [];
            if(coverPreview || coverFile) {
                postPhotos.push({
                    title: suggestedTitle || "Cover Photo",
                    previewUrl: coverPreview,
                    path: targetFirstUrl,
                    source: "upload",
                    file: coverFile,
                    order: 0
                });
            }

            if(cleanImageUrls.length > 1) {
                const maxAdditional = Math.min(cleanImageUrls.length, 10);
                const additionalUrls: { index: number; url: string; }[] = [];
                for(let i = 1; i < maxAdditional; i++) {
                    const nextUrl = cleanImageUrls[i];
                    if(nextUrl) {
                        additionalUrls.push({
                            url: nextUrl,
                            index: i
                        });
                    }
                }

                const downloadPromises = additionalUrls.map(async ({
                    url: nextUrl,
                    index
                }) => {
                    try {
                        const blob = await downloadImageBlob(nextUrl);
                        if(blob) {
                            const extension = blob.type.includes("png") ? "png" : "jpg";
                            const fileName = `instagram_${shortcode}_${index}_${Date.now()}.${extension}`;
                            const file = new File([blob], fileName, {
                                type: blob.type || "image/jpeg"
                            });
                            const previewUrl = URL.createObjectURL(blob);
                            return {
                                title: `${suggestedTitle} #${index + 1}`,
                                source: "upload" as const,
                                order: index,
                                previewUrl,
                                file
                            };
                        }
                    } catch {
                        // ignore
                    }

                    const proxyUrl = `https://wsrv.nl/?url=${encodeURIComponent(nextUrl)}`;
                    return {
                        title: `${suggestedTitle} #${index + 1}`,
                        source: "upload" as const,
                        previewUrl: proxyUrl,
                        path: nextUrl,
                        order: index
                    };
                });

                const settledResults = await Promise.allSettled(downloadPromises);
                settledResults.forEach((result) => {
                    if(result.status === "fulfilled" && result.value) {
                        postPhotos.push(result.value);
                    }
                });
            }

            onSuccess({
                coverPreview: coverPreview,
                shortContent: shortContent,
                coverPhotoPath: undefined,
                postPhotos: postPhotos,
                title: suggestedTitle,
                coverFile: coverFile,
                content: caption
            });

            NCoreUIKitSnackBar.open({
                title: localize("instagram-fetch-success")
            });
            handleReset();
        } catch (error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("global-error")
            });
        } finally {
            setIsLoading(false);
        }
    };

    return <Modal
        id="fetch-instagram-modal"
        portalName="modal-system"
        onClosed={handleReset}
        isActive={isVisible}
    >
        <View
            style={[
                stylesheet.container,
                {
                    backgroundColor: colors.project.brandWhite,
                    padding: spaces.spacingXl,
                    borderRadius: radiuses.lg,
                    gap: spaces.spacingXl
                }
            ]}
        >
            <View
                style={stylesheet.headerContainer}
            >
                <Text
                    variant="titleLargeSize"
                >
                    {localize("fetch-from-instagram")}
                </Text>
                <TouchableOpacity
                    style={stylesheet.closeButton}
                    onPress={handleReset}
                    activeOpacity={0.7}
                >
                    <XIcon
                        color={colors.content.icon.mid}
                        size={20}
                    />
                </TouchableOpacity>
            </View>
            <View
                style={stylesheet.formContainer}
            >
                <TextInput
                    placeholder="https://www.instagram.com/p/..."
                    title={localize("instagram-url-title")}
                    spreadBehaviour="stretch"
                    isDisabled={isLoading}
                    onChangeText={setUrl}
                    value={url}
                />
            </View>
            <View
                style={[
                    stylesheet.footerContainer,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Button
                    customTextColor={colors.project.brandWhite}
                    title={localize("fetch-button")}
                    spreadBehaviour="stretch"
                    onPress={handleFetch}
                    isLoading={isLoading}
                />
                <Button
                    customTextColor={colors.project.brandWhite}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    title={localize("cancel")}
                    spreadBehaviour="stretch"
                    isDisabled={isLoading}
                    onPress={handleReset}
                    variant="filled"
                />
            </View>
        </View>
    </Modal>;
};
export default FetchInstagramModal;
